/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { StyledEngineProvider } from '@mui/material/styles';
import bbox from '@turf/bbox';
import { lineString } from '@turf/helpers';
import isNil from 'lodash/isNil';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { FlightLegInfo } from '../../../../shared/entities/flightLeg';
import FlightLegServiceAlerts, { Waypoint } from '../../../../shared/entities/flightLegServiceAlerts';
import MissionPlanDetails from '../../../../shared/entities/missionPlanDetails';
import { requestBeams } from '../../../../store/beams/actions';
import { clearServiceAlerts } from '../../../../store/flightLegServiceAlerts/actions';
import { setHoveredBeam } from '../../../../store/ui/actions';
import { MapContext } from '../../../common/map/BaseMapComponent';
import { MapActionsContext } from '../../../common/map/BaseMapContainer';
import appTheme from '../../../common/theme/appTheme.json';
import { FTV_MAP_ZOOM_BUFFER } from '../../../fleet/FullTailView/FullTailViewMapZoomContainer';
import MptFlightPathContainer from '../../../fleet/MptFlightPath/MptFlightPathContainer';
import MptFlightPathWaypointsContainer from '../../../fleet/MptFlightPath/MptFlightPathWaypointsContainer';
import MptServiceAlertsContainer from '../../../fleet/MptFlightPath/MptServiceAlertsContainer';
import { CHART_SIDE_PADDING, CHART_WIDTH } from '../TimelineChart/PerformanceOutlookHorizontalTimeline';
import { getArcLightMapVersion, getViHnaMapVersion } from "../../../../utils/BeamUtils";

export const PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH = '100%';
export const PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT = '425px';

export const PERFORMANCE_OUTLOOK_MAP_TOP_PX = 104;
export const PERFORMANCE_OUTLOOK_MAP_LEFT_PX = 0;
export interface PerformanceOutlookExportMapProps {
  map?: google.maps.Map;
  flightLeg: FlightLegInfo;
  missionPlan: MissionPlanDetails;
  userId: string;
  serviceAlerts: FlightLegServiceAlerts;
}

function PerformanceOutlookExportMap({
  map,
  flightLeg,
  missionPlan,
  userId,
  serviceAlerts,
}: PerformanceOutlookExportMapProps) {
  const dispatch = useDispatch();
  const [initialGeojson, setInitialGeojson] = useState<boolean>(false);
  const overlaidFlightLegHidden = false;

  // const currentMapsOverlayState = useSelector(selectCurrentMapsOverlayState, shallowEqual);

  const mapActionsContext = useContext(MapActionsContext);
  const {
    setMapDimensions,
    clearMapDimensions,
    setMapPosition,
    clearMapPosition,
    addBeamToMap,
    clearBeamsOnMap,
    setFlightPathBeamsChecked,
    setFlightPathBeamsDisabled,
    setFlightPathWaypointsDisabled,
    setFlightPathWaypointsChecked,
    setMapHidden,
    setSelectedAntennaTypeId,
  } = mapActionsContext.mapActions;

  const setHoveredBeamHandler = useCallback(
    (beamName: string) => {
      dispatch(setHoveredBeam(beamName));
    },
    [dispatch],
  );

  const addBeamToMapHandler = useCallback(
    (name: string, networkType: string, mapType: string) => {
      dispatch(addBeamToMap(name, networkType, mapType));
    },
    [dispatch, addBeamToMap],
  );

  const resizeMap = useCallback(() => {
    const actualWidth =
      window.innerWidth - 52 <= CHART_WIDTH + 2 * CHART_SIDE_PADDING
        ? `${CHART_WIDTH + 2 * CHART_SIDE_PADDING}px`
        : PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH;
    dispatch(setMapDimensions(actualWidth, PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT));
  }, [dispatch, setMapDimensions]);

  // On mount (and unmount)
  useEffect(() => {
    window.addEventListener('resize', resizeMap);

    dispatch(clearBeamsOnMap());

    const actualWidth =
      window.innerWidth - 52 <= CHART_WIDTH + 2 * CHART_SIDE_PADDING
        ? `${CHART_WIDTH + 2 * CHART_SIDE_PADDING}px`
        : PERFORMANCE_OUTLOOK_MAP_DEFAULT_WIDTH;
    dispatch(setMapDimensions(actualWidth, PERFORMANCE_OUTLOOK_MAP_DEFAULT_HEIGHT));

    dispatch(setMapPosition(PERFORMANCE_OUTLOOK_MAP_TOP_PX, PERFORMANCE_OUTLOOK_MAP_LEFT_PX));
    dispatch(setFlightPathBeamsDisabled(false));
    dispatch(setFlightPathBeamsChecked(true));
    dispatch(setFlightPathWaypointsDisabled(false));
    dispatch(setFlightPathWaypointsChecked(false));
    return () => {
      window.removeEventListener('resize', resizeMap);

      dispatch(clearBeamsOnMap());
      dispatch(clearServiceAlerts());
      dispatch(clearMapDimensions());
      dispatch(clearMapPosition());
    };
  }, [
    dispatch,
    resizeMap,
    setMapDimensions,
    setMapPosition,
    setFlightPathBeamsDisabled,
    setFlightPathBeamsChecked,
    setFlightPathWaypointsDisabled,
    setFlightPathWaypointsChecked,
    clearBeamsOnMap,
    clearMapDimensions,
    clearMapPosition,
  ]);

  useEffect(() => {
    dispatch(setMapHidden(false));

    return () => {
      dispatch(setMapHidden(true));
    };
  }, [dispatch, setMapHidden]);

  // /servicealerts and /geojson api requests
  useEffect(() => {
    if (!isNil(missionPlan.id) && !isNil(userId) && flightLeg.id) {
      dispatch(setFlightPathBeamsChecked(true));
      dispatch(setFlightPathWaypointsChecked(false));
      if (!initialGeojson) {
        const arcLightMapVersion = getArcLightMapVersion(missionPlan.antennaTypeId, missionPlan.sscfFileVersion);
        const viHnaMapVersion = getViHnaMapVersion(missionPlan.viHnaPolicyVersion);
        dispatch(
          requestBeams(arcLightMapVersion, viHnaMapVersion),
        );
        setInitialGeojson(true);
      }
    }
  }, [
    missionPlan,
    flightLeg,
    dispatch,
    initialGeojson,
    setFlightPathBeamsChecked,
    setFlightPathWaypointsChecked,
    userId,
  ]);

  useEffect(() => {
    if (missionPlan && missionPlan.antennaTypeId) {
      dispatch(setSelectedAntennaTypeId(missionPlan.antennaTypeId));
    }
  }, [dispatch, missionPlan, setSelectedAntennaTypeId]);

  // Change zoom and location of the map to fit flight leg
  useEffect(() => {
    if (serviceAlerts?.waypoints) {
      const flightPathBbox = bbox(
        lineString(serviceAlerts.waypoints.map((waypoint: Waypoint) => [waypoint.longitude, waypoint.latitude])),
      );
      if (map && map.fitBounds) {
        map.fitBounds(
          {
            east: flightPathBbox[2],
            north: flightPathBbox[3],
            south: flightPathBbox[1],
            west: flightPathBbox[0],
          } as google.maps.LatLngBoundsLiteral,
          FTV_MAP_ZOOM_BUFFER,
        );
      }
    }
  }, [dispatch, serviceAlerts, map, flightLeg.id]);

  return (
    <React.Fragment>
      <MapContext.Consumer>
        {map => (
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <React.Fragment>
                <MptFlightPathWaypointsContainer
                  map={map}
                  flightLeg={flightLeg}
                  flightLegServiceAlerts={{ [flightLeg.id]: serviceAlerts }}
                />
                <MptFlightPathContainer
                  map={map}
                  flightLeg={flightLeg}
                  flightLegServiceAlerts={{ [flightLeg.id]: serviceAlerts }}
                  addBeamToMap={addBeamToMapHandler}
                />

                <MptServiceAlertsContainer
                  map={map}
                  flightLeg={flightLeg}
                  flightLegServiceAlerts={{ [flightLeg.id]: serviceAlerts }}
                  isRequestingFlightLegServiceAlerts={false}
                  overlaidFlightLegHidden={overlaidFlightLegHidden}
                  setHoveredBeam={setHoveredBeamHandler}
                  mapId={`exportMap-${flightLeg.id}`}
                />
              </React.Fragment>
            </ThemeProvider>
          </StyledEngineProvider>
        )}
      </MapContext.Consumer>
    </React.Fragment>
  );
}

export default PerformanceOutlookExportMap;
